import React, { useState, useEffect } from "react";
import { FaUsers } from "react-icons/fa";
import { Spinner } from "react-bootstrap";
import TagAutocomplete from "./TagAutocomplete";
import api from "../api";
import "./style/LoginPromptModal.css";
import "./style/GuestGalleryShareModal.css";

/**
 * Modal to share a main-gallery photo to the Event Wall,
 * with optional caption and @tag mentions.
 *
 * Props:
 *   isOpen      bool
 *   onClose     () => void
 *   imageName   string   — filename used for the API call
 *   imageUrl    string   — thumbnail src for preview
 *   eventId     number
 */
export default function GuestGalleryShareModal({ isOpen, onClose, imageName, imageUrl, eventId }) {
  const [caption, setCaption] = useState("");
  const [tagInput, setTagInput] = useState("");
  const [tags, setTags] = useState([]); // [{user_id, display_name}]
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Lock body scroll when open
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setCaption("");
      setTagInput("");
      setTags([]);
      setError(null);
      setSubmitting(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  function handleTagSelect(user) {
    if (!tags.find((t) => t.user_id === user.user_id)) {
      setTags((prev) => [...prev, user]);
    }
    setTagInput("");
  }

  function removeTag(userId) {
    setTags((prev) => prev.filter((t) => t.user_id !== userId));
  }

  async function handleSubmit() {
    if (submitting) return;
    setSubmitting(true);
    setError(null);

    try {
      // 1. Share photo to event wall
      const res = await api.post(
        `/social/share?eid=${eventId}&image_name=${encodeURIComponent(imageName)}` +
          (caption.trim() ? `&caption=${encodeURIComponent(caption.trim())}` : "")
      );

      if (!res.data.success) {
        setError("Could not share the photo. Please try again.");
        setSubmitting(false);
        return;
      }

      const socialPhotoId = res.data.photo?.social_photo_id;

      // 2. Tag each selected user
      if (socialPhotoId && tags.length > 0) {
        await Promise.allSettled(
          tags.map((t) =>
            api.post(`/social/tag/${socialPhotoId}?tagged_user_id=${t.user_id}`)
          )
        );
      }

      // Success toast
      const toast = document.createElement("div");
      toast.textContent = "Added to the Event Wall!";
      Object.assign(toast.style, {
        position: "fixed", bottom: "24px", left: "50%", transform: "translateX(-50%)",
        background: "#262626", color: "#fff", padding: "10px 20px", borderRadius: "8px",
        fontSize: "14px", zIndex: 9999, pointerEvents: "none",
      });
      document.body.appendChild(toast);
      setTimeout(() => document.body.removeChild(toast), 2800);

      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || "Something went wrong. Please try again.");
    }
    setSubmitting(false);
  }

  return (
    <div className="lpm-overlay" onClick={onClose}>
      <div className="ggm-card" onClick={(e) => e.stopPropagation()}>
        {/* Close */}
        <button className="lpm-close" onClick={onClose} aria-label="Close">✕</button>

        {/* Header */}
        <div className="ggm-header">
          <FaUsers size={20} color="#595959" />
          <span className="ggm-title">Share to Event Wall</span>
        </div>

        {/* Thumbnail */}
        {imageUrl && (
          <div className="ggm-preview-wrapper">
            <img src={imageUrl} alt="Preview" className="ggm-preview" />
          </div>
        )}

        {/* Caption */}
        <textarea
          className="ggm-caption"
          placeholder="Write a caption… (optional)"
          rows={2}
          maxLength={500}
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
        />

        {/* Tag input */}
        <div className="ggm-tag-section">
          <TagAutocomplete
            eventId={eventId}
            value={tagInput}
            onChange={setTagInput}
            onSelect={handleTagSelect}
            placeholder="Tag someone (@name) — optional"
          />

          {/* Tag chips */}
          {tags.length > 0 && (
            <div className="ggm-chips">
              {tags.map((t) => (
                <span key={t.user_id} className="ggm-chip">
                  @{t.display_name}
                  <button
                    className="ggm-chip-remove"
                    onClick={() => removeTag(t.user_id)}
                    aria-label={`Remove tag ${t.display_name}`}
                  >
                    ✕
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {error && <p className="ggm-error">{error}</p>}

        {/* Actions */}
        <div className="ggm-actions">
          <button className="lpm-btn lpm-btn--secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button className="lpm-btn lpm-btn--primary ggm-submit-btn" onClick={handleSubmit} disabled={submitting}>
            {submitting ? <Spinner animation="border" size="sm" /> : "Share to Gallery"}
          </button>
        </div>
      </div>
    </div>
  );
}
